(() => {
  const isTouch = window.matchMedia('(max-width: 860px)').matches;
  // Shopify theme editor: no preloader, no popup, sections re-rendered live.
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const routes = (window.AEP && window.AEP.routes) || {
    root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update', predictiveSearch: '/search/suggest',
  };
  gsap.registerPlugin(ScrollTrigger);

  /* ------------------------------------------------------------------ */
  /* Smooth scroll (Lenis + GSAP ticker)                                */
  /* ------------------------------------------------------------------ */
  let lenis;
  if (!isTouch && window.Lenis) {
    lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  }
  const scrollTo = (target) => {
    const offset = target === 0 ? 0 : -90;
    if (lenis) lenis.scrollTo(target, { duration: 1.1, offset });
    else window.scrollTo({ top: (target === 0 ? 0 : target.offsetTop) + offset, behavior: 'smooth' });
  };

  function flashTarget(el) {
    el.classList.remove('flash-target');
    void el.offsetWidth;
    el.classList.add('flash-target');
    setTimeout(() => el.classList.remove('flash-target'), 1800);
  }


  // Links to an anchor of the current page scroll smoothly; any other link loads normally.
  function isSamePageAnchor(href) {
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin && url.pathname === location.pathname && url.hash.length > 1;
    } catch (e) {
      return false;
    }
  }
  function findTarget(hash) {
    try { return document.querySelector(hash); } catch (e) { return null; }
  }
  function goTo(href) {
    if (!isSamePageAnchor(href)) { window.location.href = href; return; }
    const { hash } = new URL(href, location.href);
    const target = findTarget(hash);
    if (!target) { window.location.href = href; return; }
    history.pushState(null, '', hash);
    scrollTo(target);
    if (target.classList.contains('product-cell')) flashTarget(target);
  }

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ));
  // Prices follow the shop's money format (Settings → General), like the Liquid money filters:
  // the cart drawer shows exactly what the product grid shows. Trailing zeros are dropped ("310 €").
  const moneyFormat = (() => {
    const raw = document.createElement('textarea');
    raw.innerHTML = (window.AEP && window.AEP.moneyFormat) || '{{amount_with_space_separator}} €';
    return raw.value;
  })();
  function money(cents) {
    const value = (Number(cents) || 0) / 100;
    const build = (decimals, thousands, decimal) => {
      const [int, dec] = value.toFixed(decimals).split('.');
      const whole = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec && Number(dec) !== 0 ? whole + decimal + dec : whole;
    };
    const formats = {
      amount: () => build(2, ',', '.'),
      amount_no_decimals: () => build(0, ',', '.'),
      amount_with_comma_separator: () => build(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => build(0, '.', ','),
      amount_with_space_separator: () => build(2, ' ', ','),
      amount_no_decimals_with_space_separator: () => build(0, ' ', ','),
      amount_with_apostrophe_separator: () => build(2, "'", '.'),
      amount_with_period_and_space_separator: () => build(2, ' ', '.'),
    };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => (formats[key] || formats.amount)());
  }
  // Shopify CDN images can be requested at the size needed.
  const sizedImage = (url, width) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '');

  const toast = document.getElementById('toast');
  let toastTimer;
  function showToast(text) {
    toast.textContent = text;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2200);
  }

  /* ------------------------------------------------------------------ */
  /* Preloader                                                          */
  /* ------------------------------------------------------------------ */
  const preloader = document.getElementById('preloader');
  const preloaderNum = document.getElementById('preloader-num');
  const preloaderFill = document.getElementById('preloader-fill');
  const hasHero = !!document.querySelector('.hero-line');

  function playHeroIntro() {
    if (hasHero) {
      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });
      tl.to('.hero-line', { yPercent: 0, duration: 1.1 }, 0)
        .to('.hero-sub p', { opacity: 1, y: 0, duration: 0.8 }, 0.4)
        .to('.hero-scroll, .hero-scroll-accent', { opacity: 1, y: 0, duration: 0.8 }, 0.5)
        .to(['.hero-img-left img', '.hero-img-right img'], { scale: 1.06, duration: 1.6, ease: 'power2.out' }, 0);
    }
    ScrollTrigger.refresh();
  }

  let resolvePreloader;
  const preloaderFinished = new Promise((resolve) => { resolvePreloader = resolve; });

  if (designMode) {
    preloader.style.display = 'none';
    window.addEventListener('load', () => { playHeroIntro(); resolvePreloader(); });
  } else {
    const progress = { v: 0 };
    window.addEventListener('load', () => {
      gsap.to(progress, {
        v: 100,
        duration: 1.3,
        ease: 'power2.inOut',
        onUpdate: () => {
          const n = Math.round(progress.v);
          preloaderNum.textContent = n;
          preloaderFill.style.width = n + '%';
        },
        onComplete: () => {
          gsap.to(preloader, {
            yPercent: -100,
            duration: 0.9,
            ease: 'power4.inOut',
            delay: 0.25,
            onComplete: () => {
              preloader.style.display = 'none';
              playHeroIntro();
              resolvePreloader();
            },
          });
        },
      });
    });
    // safety net if 'load' never settles quickly
    setTimeout(() => { if (preloader.style.display !== 'none') window.dispatchEvent(new Event('load')); }, 3500);
  }

  // Arriving from another page on an anchor (e.g. /#collection): scroll there once the page is shown.
  preloaderFinished.then(() => {
    const target = location.hash.length > 1 && findTarget(location.hash);
    if (target && !target.closest('.newsletter-popup')) {
      scrollTo(target);
      if (target.classList.contains('product-cell')) flashTarget(target);
    }
  });

  if (hasHero) {
    gsap.set('.hero-line', { yPercent: 110 });
    gsap.set('.hero-sub p, .hero-scroll, .hero-scroll-accent', { opacity: 0, y: 16 });
  }

  /* ------------------------------------------------------------------ */
  /* Custom cursor                                                      */
  /* ------------------------------------------------------------------ */
  const cursorDot = document.getElementById('cursorDot');
  const cursorRing = document.getElementById('cursorRing');
  const cursorLabel = document.getElementById('cursorLabel');
  let bindHoverCursor = () => {};

  if (!isTouch) {
    document.documentElement.classList.add('has-custom-cursor');
    const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ring = { x: mouse.x, y: mouse.y };
    window.addEventListener('mousemove', (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      if (cursorDot.classList.contains('is-hidden')) showCursor();
      cursorDot.style.transform = `translate(${mouse.x}px, ${mouse.y}px) translate(-50%, -50%)`;
    });
    gsap.ticker.add(() => {
      ring.x += (mouse.x - ring.x) * 0.18;
      ring.y += (mouse.y - ring.y) * 0.18;
      cursorRing.style.transform = `translate(${ring.x}px, ${ring.y}px) translate(-50%, -50%)`;
    });

    const setCursor = (active, label, accent) => {
      cursorRing.classList.toggle('is-active', !!active);
      cursorRing.classList.toggle('is-accent', !!accent);
      cursorLabel.textContent = label || '';
    };

    bindHoverCursor = (container) => {
      container.querySelectorAll('[data-hover]').forEach((el) => {
        el.addEventListener('mouseenter', () => setCursor(true, ''));
        el.addEventListener('mouseleave', () => setCursor(false, ''));
      });
      container.querySelectorAll('.product-cell-img').forEach((el) => {
        el.addEventListener('mouseenter', () => setCursor(true, ''));
        el.addEventListener('mouseleave', () => setCursor(false, ''));
      });
      container.querySelectorAll('.add-to-cart').forEach((el) => {
        el.addEventListener('mouseenter', () => setCursor(true, '', true));
        el.addEventListener('mouseleave', () => setCursor(false, ''));
      });
    };
    bindHoverCursor(document);
    // Hidden when the pointer leaves the window; any movement, focus or page restore brings it back.
    function showCursor() {
      cursorDot.classList.remove('is-hidden');
      cursorRing.classList.remove('is-hidden');
    }
    document.documentElement.addEventListener('mouseleave', () => {
      cursorDot.classList.add('is-hidden');
      cursorRing.classList.add('is-hidden');
    });
    document.documentElement.addEventListener('mouseenter', showCursor);
    window.addEventListener('focus', showCursor);
    window.addEventListener('pageshow', () => { showCursor(); setCursor(false, ''); });
  }

  /* ------------------------------------------------------------------ */
  /* Magnetic buttons                                                   */
  /* ------------------------------------------------------------------ */
  if (!isTouch) {
    document.querySelectorAll('.nav-logo, .back-to-top, .newsletter-form button, .cart-checkout').forEach((el) => {
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        gsap.to(el, { x: x * 0.35, y: y * 0.35, duration: 0.4, ease: 'power3.out' });
      });
      el.addEventListener('mouseleave', () => {
        gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scroll progress + nav hide/show                                    */
  /* ------------------------------------------------------------------ */
  const scrollProgressEl = document.getElementById('scrollProgress');
  const nav = document.getElementById('nav');
  const backToTop = document.getElementById('backToTop');
  let lastY = window.scrollY;

  function onScrollUpdate() {
    const y = window.scrollY;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    scrollProgressEl.style.width = (max > 0 ? (y / max) * 100 : 0) + '%';

    const delta = y - lastY;
    if (Math.abs(delta) > 6) {
      if (delta > 0 && y > 120) nav.classList.add('nav-hidden');
      else if (delta < 0) nav.classList.remove('nav-hidden');
      lastY = y;
    }
    if (y <= 120) nav.classList.remove('nav-hidden');

    backToTop.classList.toggle('is-visible', y > window.innerHeight * 0.6);
  }

  if (lenis) lenis.on('scroll', onScrollUpdate);
  else window.addEventListener('scroll', onScrollUpdate);
  window.addEventListener('resize', () => ScrollTrigger.refresh());

  backToTop.addEventListener('click', () => scrollTo(0));

  /* ------------------------------------------------------------------ */
  /* Scroll reveals                                                     */
  /* ------------------------------------------------------------------ */
  function setupReveal(root) {
    root.querySelectorAll('[data-reveal]').forEach((el) => {
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%' },
      });
    });
  }
  setupReveal(document);

  /* ------------------------------------------------------------------ */
  /* Parallax                                                           */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('[data-parallax] img').forEach((img) => {
    // The photos are wider than their half: they drift apart slightly while scrolling.
    const drift = img.closest('.hero-img-left') ? -4 : 4;
    gsap.fromTo(
      img,
      { yPercent: -8, xPercent: 0 },
      {
        yPercent: 8,
        xPercent: drift,
        ease: 'none',
        scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true },
      }
    );
  });
  document.querySelectorAll('[data-parallax-slow] img').forEach((img) => {
    gsap.fromTo(
      img,
      { yPercent: -10 },
      {
        yPercent: 10,
        ease: 'none',
        scrollTrigger: { trigger: '.savoir-faire', start: 'top bottom', end: 'bottom top', scrub: true },
      }
    );
  });

  /* ------------------------------------------------------------------ */
  /* Marquee                                                            */
  /* ------------------------------------------------------------------ */
  const marqueeTrack = document.getElementById('marqueeTrack');
  if (marqueeTrack) {
    // Same reading speed whatever the length of the text (about 75 px per second).
    const duration = Math.max(12, marqueeTrack.scrollWidth / 2 / 75);
    gsap.to(marqueeTrack, { xPercent: -50, repeat: -1, duration, ease: 'linear' });
  }

  /* ------------------------------------------------------------------ */
  /* Horizontal pinned showcase                                         */
  /* ------------------------------------------------------------------ */
  // The khaki band stays on screen while the words cross it, then the photos follow below.
  const hTrack = document.getElementById('hTrack');
  if (hTrack && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const hPin = hTrack.parentElement;
    hTrack.style.paddingLeft = '100vw'; // the words enter from the right
    const hDistance = () => hTrack.scrollWidth - window.innerWidth;
    // The words lean with the scroll speed and straighten up when it stops.
    const skewTo = gsap.quickTo(hTrack, 'skewX', { duration: 0.5, ease: 'power3' });
    let skewReset;
    gsap.timeline({
      scrollTrigger: {
        trigger: hPin,
        start: 'top top',
        end: () => '+=' + Math.round(hDistance() * 0.7),
        scrub: 1,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          skewTo(gsap.utils.clamp(-10, 10, self.getVelocity() / -250));
          if (skewReset) skewReset.kill();
          skewReset = gsap.delayedCall(0.15, () => skewTo(0));
        },
      },
    })
      .to(hTrack, { x: () => -hDistance(), ease: 'none' }, 0)
      .to(hPin.querySelectorAll('.cross-icon'), { rotation: 720, ease: 'none' }, 0);
  }

  /* ------------------------------------------------------------------ */
  /* Editorial tile tilt                                                */
  /* ------------------------------------------------------------------ */
  // Any mouse, whatever the window width when the page was loaded.
  document.querySelectorAll('[data-tilt]').forEach((tile) => {
    tile.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const r = tile.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      gsap.to(tile, {
        rotateX: py * -10,
        rotateY: px * 10,
        transformPerspective: 600,
        duration: 0.5,
        ease: 'power2.out',
      });
    });
    tile.addEventListener('pointerleave', () => {
      gsap.to(tile, { rotateX: 0, rotateY: 0, duration: 0.6, ease: 'power3.out' });
    });
  });

  /* ------------------------------------------------------------------ */
  /* Savoir-faire — interactive cross field                             */
  /* ------------------------------------------------------------------ */
  const crossField = document.getElementById('sfCrossField');
  if (crossField) {
    const svgNS = 'http://www.w3.org/2000/svg';
    const spacing = 80;
    let crosses = [];

    // The grid follows the hero's current size, so it is rebuilt whenever the window is resized.
    function buildCrossField() {
      crossField.replaceChildren();
      crosses = [];
      const fieldRect = crossField.getBoundingClientRect();
      const cols = Math.ceil(fieldRect.width / spacing) + 1;
      const rows = Math.ceil(fieldRect.height / spacing) + 1;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const svg = document.createElementNS(svgNS, 'svg');
          svg.setAttribute('viewBox', '0 0 24 24');
          svg.classList.add('sf-cross');
          const size = 10 + Math.random() * 8;
          const baseX = c * spacing + (Math.random() - 0.5) * spacing * 0.6;
          const baseY = r * spacing + (Math.random() - 0.5) * spacing * 0.6;
          const rot = Math.random() * 40 - 20;
          svg.style.width = size + 'px';
          svg.style.height = size + 'px';
          svg.style.left = baseX + 'px';
          svg.style.top = baseY + 'px';
          svg.style.opacity = 0.35 + Math.random() * 0.5;

          const path = document.createElementNS(svgNS, 'path');
          path.setAttribute('d', 'M10 2H14V10H22V14H14V22H10V14H2V10H10V2Z');
          path.setAttribute('fill', 'currentColor');
          svg.appendChild(path);
          crossField.appendChild(svg);

          crosses.push({ el: svg, baseX, baseY, rot, x: 0, y: 0, s: 1, rCur: rot });
        }
      }
    }
    buildCrossField();

    let lastFieldWidth = crossField.offsetWidth;
    let lastFieldHeight = crossField.offsetHeight;
    let rebuildTimer;
    window.addEventListener('resize', () => {
      clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(() => {
        if (crossField.offsetWidth === lastFieldWidth && crossField.offsetHeight === lastFieldHeight) return;
        lastFieldWidth = crossField.offsetWidth;
        lastFieldHeight = crossField.offsetHeight;
        buildCrossField();
      }, 150);
    });

    // Driven by the pointer itself, not the window width: a mouse in a small window still gets the effect.
    const hasMouse = window.matchMedia('(hover: hover) and (pointer: fine)');
    const mouse = { x: -9999, y: -9999 };
    crossField.addEventListener('mousemove', (e) => {
      const r = crossField.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    });
    crossField.addEventListener('mouseleave', () => {
      mouse.x = -9999;
      mouse.y = -9999;
    });

    const radius = 150;
    gsap.ticker.add(() => {
      if (!hasMouse.matches) return;
      crosses.forEach((cross) => {
        const dx = cross.baseX - mouse.x;
        const dy = cross.baseY - mouse.y;
        const dist = Math.hypot(dx, dy);
        let tx = 0, ty = 0, ts = 1, tr = cross.rot;
        if (dist < radius) {
          const push = 1 - dist / radius;
          const angle = Math.atan2(dy, dx);
          tx = Math.cos(angle) * push * 26;
          ty = Math.sin(angle) * push * 26;
          ts = 1 + push * 1.1;
          tr = cross.rot + push * 50;
        }
        cross.x += (tx - cross.x) * 0.15;
        cross.y += (ty - cross.y) * 0.15;
        cross.s += (ts - cross.s) * 0.15;
        cross.rCur += (tr - cross.rCur) * 0.15;
        cross.el.style.transform = `translate(${cross.x}px, ${cross.y}px) rotate(${cross.rCur}deg) scale(${cross.s})`;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Journal page — editorial motion                                     */
  /* ------------------------------------------------------------------ */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Wraps each word in <span class="wrapClass"><span>word</span></span>, keeping child elements such as <em>.
  function splitWords(el, wrapClass) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const frag = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        const outer = document.createElement('span');
        const inner = document.createElement('span');
        outer.className = wrapClass;
        inner.textContent = part;
        outer.appendChild(inner);
        frag.appendChild(outer);
      });
      node.parentNode.replaceChild(frag, node);
    });
    return el.querySelectorAll(`.${wrapClass} > span`);
  }

  function setupJournal(root) {
    const journal = root.querySelector('.journal');
    if (!journal || journal.dataset.ready) return;
    journal.dataset.ready = '1';

    // "Explorer nos collections": arrows scroll one card; swipe works natively; arrows off at the ends.
    journal.querySelectorAll('[data-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-carousel-track]');
      const prev = carousel.querySelector('[data-carousel-prev]');
      const next = carousel.querySelector('[data-carousel-next]');
      if (!track || !prev || !next) return;
      const step = () => {
        const card = track.firstElementChild;
        return card ? card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0) : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
        carousel.classList.toggle('is-static', max <= 0);
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });

    // Opening: the title lines rise and the photos appear once the page is shown.
    const heroLines = journal.querySelectorAll('.jn-line > span');
    const heroExtras = journal.querySelectorAll('.jn-hero-label, .jn-hero-caption');
    const heroImgs = journal.querySelectorAll('.jn-hero-img');
    if (heroLines.length && !reduceMotion) {
      gsap.set(heroLines, { yPercent: 110 });
      gsap.set([...heroExtras, ...heroImgs], { opacity: 0 });
      preloaderFinished.then(() => {
        gsap.to(heroLines, { yPercent: 0, duration: 1.2, ease: 'power4.out', stagger: 0.14 });
        gsap.to(heroExtras, { opacity: 1, duration: 0.8, delay: 0.5 });
        gsap.fromTo(heroImgs, { scale: 1.08 }, { opacity: 1, scale: 1, duration: 1.4, ease: 'power3.out', stagger: 0.15, delay: 0.15 });
      });
    }

    // The statement lights up word by word while scrolling.
    journal.querySelectorAll('[data-words]').forEach((el) => {
      const words = splitWords(el, 'jn-word');
      if (reduceMotion) return;
      gsap.fromTo(words, { opacity: 0.14 }, {
        opacity: 1,
        stagger: 0.05,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top 80%', end: 'bottom 45%', scrub: true },
      });
    });

    // Headings: words rise one after the other.
    journal.querySelectorAll('[data-split]').forEach((el) => {
      const words = splitWords(el, 'jn-w');
      if (reduceMotion) return;
      gsap.fromTo(words, { yPercent: 110 }, {
        yPercent: 0,
        duration: 1,
        ease: 'power4.out',
        stagger: 0.06,
        scrollTrigger: { trigger: el, start: 'top 88%' },
      });
    });

    if (reduceMotion) return;

    // Photos uncover from the bottom.
    journal.querySelectorAll('[data-clip]').forEach((el) => {
      const trigger = { trigger: el, start: 'top 85%' };
      gsap.fromTo(el, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, ease: 'power3.inOut', scrollTrigger: trigger });
      const img = el.querySelector('img');
      if (img) gsap.fromTo(img, { scale: 1.25 }, { scale: 1, duration: 1.8, ease: 'power3.out', scrollTrigger: { ...trigger } });
    });

    // Photos drift at different speeds (not on touch screens).
    if (!isTouch) {
      journal.querySelectorAll('[data-speed]').forEach((el) => {
        gsap.to(el, {
          yPercent: -(parseFloat(el.dataset.speed) || 0) * 100,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      });
    }

    // Big sentences: filled from left to right, or sliding sideways.
    journal.querySelectorAll('.jn-quote--fill').forEach((quote) => {
      const solid = quote.querySelector('.jn-quote-solid');
      if (!solid) return;
      gsap.fromTo(solid, { clipPath: 'inset(0% 100% 0% 0%)' }, {
        clipPath: 'inset(0% 0% 0% 0%)',
        ease: 'none',
        scrollTrigger: { trigger: quote, start: 'top 75%', end: 'bottom 35%', scrub: true },
      });
    });
    // The sliding sentence crosses the whole screen, from its first word to its last.
    journal.querySelectorAll('.jn-quote--slide .jn-quote-text').forEach((text) => {
      gsap.fromTo(text, { x: () => window.innerWidth * 0.1 }, {
        x: () => -(text.offsetWidth - window.innerWidth * 0.9),
        ease: 'none',
        scrollTrigger: { trigger: text, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
      });
    });

    // Key figures grow in.
    journal.querySelectorAll('.jn-figure-value').forEach((value) => {
      gsap.fromTo(value, { scale: 0.6, transformOrigin: 'left bottom' }, {
        scale: 1,
        duration: 1.2,
        ease: 'power4.out',
        scrollTrigger: { trigger: value, start: 'top 90%' },
      });
    });
  }
  setupJournal(document);

  /* ------------------------------------------------------------------ */
  /* Mobile menu                                                        */
  /* ------------------------------------------------------------------ */
  const burger = document.getElementById('burgerToggle');
  const menuOverlay = document.getElementById('menuOverlay');
  let menuOpen = false;
  gsap.set('.menu-overlay-link span', { y: 70, opacity: 0 });
  const menuTl = gsap.timeline({ paused: true })
    .to(menuOverlay, { clipPath: 'inset(0 0 0% 0)', duration: 0.6, ease: 'power4.inOut' })
    .to('.menu-overlay-link span', { y: 0, opacity: 1, stagger: 0.06, duration: 0.7, ease: 'power4.out' }, 0.2);

  const menuTrigger = document.getElementById('menuCollectionTrigger');
  const menuSubmenu = document.getElementById('menuSubmenu');
  const menuItem = menuTrigger && menuTrigger.closest('.menu-item');
  let submenuOpen = false;

  function openSubmenu() {
    if (submenuOpen) return;
    submenuOpen = true;
    menuTrigger.setAttribute('aria-expanded', 'true');
    gsap.to(menuSubmenu, { height: menuSubmenu.querySelector('.menu-submenu-inner').offsetHeight, duration: 0.5, ease: 'power3.inOut' });
  }

  function closeSubmenu() {
    if (!submenuOpen) return;
    submenuOpen = false;
    menuTrigger.setAttribute('aria-expanded', 'false');
    gsap.to(menuSubmenu, { height: 0, duration: 0.4, ease: 'power3.inOut' });
  }

  if (menuItem) {
    menuItem.addEventListener('mouseenter', openSubmenu);
    menuItem.addEventListener('mouseleave', closeSubmenu);
    menuTrigger.addEventListener('click', openSubmenu);
  }

  burger.addEventListener('click', () => {
    menuOpen = !menuOpen;
    burger.classList.toggle('is-open', menuOpen);
    menuOverlay.classList.toggle('is-open', menuOpen);
    if (menuOpen) {
      menuTl.play();
    } else {
      menuTl.reverse();
      closeSubmenu();
    }
  });
  menuOverlay.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => {
    menuOpen = false;
    burger.classList.remove('is-open');
    menuOverlay.classList.remove('is-open');
    menuTl.reverse();
    closeSubmenu();
  }));

  /* ------------------------------------------------------------------ */
  /* Search overlay — Shopify predictive search                         */
  /* ------------------------------------------------------------------ */
  const searchToggle = document.getElementById('searchToggle');
  const searchOverlay = document.getElementById('searchOverlay');
  const searchClose = document.getElementById('searchClose');
  const searchForm = document.getElementById('searchForm');
  const searchInput = document.getElementById('searchInput');
  const searchResults = document.getElementById('searchResults');
  let searchTimer;
  let searchController;

  async function runSearch(query) {
    const q = query.trim();
    if (!q) {
      searchResults.innerHTML = '';
      return;
    }
    if (searchController) searchController.abort();
    searchController = new AbortController();
    const params = new URLSearchParams({
      q,
      'resources[type]': 'product,collection,page',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
    });
    try {
      const res = await fetch(`${routes.predictiveSearch}.json?${params}`, {
        signal: searchController.signal, headers: { Accept: 'application/json' },
      });
      const found = (await res.json()).resources?.results || {};
      const matches = [
        ...(found.products || []).map((p) => ({ label: p.title, type: 'Sac', url: p.url })),
        ...(found.collections || []).map((c) => ({ label: c.title, type: 'Catégorie', url: c.url })),
        ...(found.pages || []).map((p) => ({ label: p.title, type: 'Page', url: p.url })),
      ];
      if (!matches.length) {
        searchResults.innerHTML = '<p class="search-empty">Aucun résultat.</p>';
        return;
      }
      searchResults.innerHTML = matches
        .map((item) => `<a href="${escapeHtml(item.url)}" class="search-result" data-hover><span>${escapeHtml(item.label)}</span><span class="search-result-type">${escapeHtml(item.type)}</span></a>`)
        .join('');
      bindHoverCursor(searchResults);
    } catch (err) {
      if (err.name !== 'AbortError') searchResults.innerHTML = '<p class="search-empty">La recherche est momentanément indisponible.</p>';
    }
  }

  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => runSearch(searchInput.value), 180);
  });
  // Enter opens the first suggestion; without suggestions, Shopify's full search page opens.
  searchForm.addEventListener('submit', (e) => {
    const first = searchResults.querySelector('.search-result');
    if (first) {
      e.preventDefault();
      window.location.href = first.href;
    }
  });
  searchToggle.addEventListener('click', () => {
    searchOverlay.classList.add('is-open');
    setTimeout(() => searchInput.focus(), 300);
  });
  searchClose.addEventListener('click', () => {
    searchOverlay.classList.remove('is-open');
    searchInput.value = '';
    searchResults.innerHTML = '';
  });

  /* ------------------------------------------------------------------ */
  /* Cart — Shopify cart (Ajax API): drawer, recap, then Shopify checkout */
  /* ------------------------------------------------------------------ */
  const cartToggle = document.getElementById('cartToggle');
  const cartClose = document.getElementById('cartClose');
  const cartDrawer = document.getElementById('cartDrawer');
  const cartBackdrop = document.getElementById('cartBackdrop');
  const cartItemsEl = document.getElementById('cartItems');
  const cartTotalEl = document.getElementById('cartTotal');
  const cartCountEl = document.getElementById('cartCount');
  const cartTitleEl = document.getElementById('cartTitle');
  const cartRecapEl = document.getElementById('cartRecap');
  const cartNextBtn = document.getElementById('cartNext');
  const cartBackBtn = document.getElementById('cartBack');
  const cartOrderBtn = document.getElementById('cartOrder');
  const cartNoteWrap = document.getElementById('cartNoteWrap');
  const cartNoteEl = document.getElementById('cartNote');
  let cart = { items: [], item_count: Number(cartCountEl.textContent) || 0, total_price: 0 };
  let recapOpen = false;

  async function cartRequest(url, body) {
    const res = await fetch(url, body === undefined
      ? { headers: { Accept: 'application/json' } }
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Le panier n’a pas pu être mis à jour. Réessayez.');
    return data;
  }
  async function loadCart() {
    cart = await cartRequest(`${routes.cart}.js`);
    renderCart();
  }

  function openCart() {
    cartDrawer.classList.add('is-open');
    cartBackdrop.classList.add('is-open');
  }
  function closeCart() {
    cartDrawer.classList.remove('is-open');
    cartBackdrop.classList.remove('is-open');
    showCartStep();
  }
  cartToggle.addEventListener('click', openCart);
  cartClose.addEventListener('click', closeCart);
  cartBackdrop.addEventListener('click', closeCart);

  function renderCart() {
    cartCountEl.textContent = cart.item_count;
    if (!cart.items.length) {
      cartItemsEl.innerHTML = '<p class="cart-empty">Votre panier est vide.</p>';
      cartTotalEl.textContent = money(0);
      syncRecap();
      return;
    }
    cartItemsEl.innerHTML = cart.items
      .map((it) => `<div class="cart-item"><span>${escapeHtml(it.product_title)}${it.variant_title ? ` <span class="cart-item-qty">${escapeHtml(it.variant_title)}</span>` : ''} <span class="cart-item-qty">× ${it.quantity}</span></span><span class="cart-item-side"><span>${escapeHtml(money(it.final_line_price))}</span><button type="button" class="cart-item-remove" data-hover data-key="${escapeHtml(it.key)}" aria-label="Supprimer ${escapeHtml(it.product_title)} du panier">Supprimer</button></span></div>`)
      .join('');
    bindHoverCursor(cartItemsEl);
    cartTotalEl.textContent = money(cart.total_price);
    syncRecap();
  }

  // Recap step — reached only through "Suivant"; photos, unit prices and totals come from Shopify's cart.
  function renderRecap() {
    cartRecapEl.innerHTML = cart.items.map((it) => `
      <div class="recap-item">
        <div class="recap-item-img">${it.image ? `<img src="${escapeHtml(sizedImage(it.image, 200))}" alt="${escapeHtml(it.product_title)}">` : ''}</div>
        <div class="recap-item-info">
          <span class="recap-item-name">${escapeHtml(it.product_title)}</span>
          <span class="recap-item-meta">${escapeHtml(money(it.final_price))} × ${it.quantity}</span>
        </div>
        <span class="recap-item-subtotal">${escapeHtml(money(it.final_line_price))}</span>
      </div>`).join('');
  }
  function showCartStep() {
    recapOpen = false;
    cartTitleEl.textContent = 'Panier';
    cartItemsEl.hidden = false;
    cartRecapEl.hidden = true;
    cartNextBtn.hidden = false;
    cartBackBtn.hidden = true;
    cartOrderBtn.hidden = true;
    if (cartNoteWrap) cartNoteWrap.hidden = true;
  }
  function showRecapStep() {
    if (!cart.items.length) return;
    recapOpen = true;
    renderRecap();
    cartTitleEl.textContent = 'Récapitulatif';
    cartItemsEl.hidden = true;
    cartRecapEl.hidden = false;
    cartNextBtn.hidden = true;
    cartBackBtn.hidden = false;
    cartOrderBtn.hidden = false;
    if (cartNoteWrap) cartNoteWrap.hidden = false;
    cartRecapEl.scrollTop = 0;
  }
  // Keeps the recap in step with the cart; an emptied cart sends the drawer back to the cart step.
  function syncRecap() {
    cartNextBtn.disabled = !cart.items.length;
    if (!recapOpen) return;
    if (cart.items.length) renderRecap();
    else showCartStep();
  }
  cartNextBtn.addEventListener('click', showRecapStep);
  cartBackBtn.addEventListener('click', showCartStep);
  // "Commander": the optional order note is saved on the cart, then Shopify's secure checkout opens
  // (address, delivery, card / PayPal payment).
  cartOrderBtn.addEventListener('click', async () => {
    if (cartNoteEl && cartNoteEl.value !== (cart.note || '')) {
      cartOrderBtn.disabled = true;
      try {
        cart = await cartRequest(`${routes.cartUpdate || '/cart/update'}.js`, { note: cartNoteEl.value });
      } catch (err) {
        showToast(err.message);
        cartOrderBtn.disabled = false;
        return;
      }
    }
    window.location.href = '/checkout';
  });

  cartItemsEl.addEventListener('click', async (e) => {
    const btn = e.target.closest('.cart-item-remove');
    if (!btn) return;
    const item = cart.items.find((it) => it.key === btn.dataset.key);
    btn.disabled = true;
    try {
      cart = await cartRequest(`${routes.cartChange}.js`, { id: btn.dataset.key, quantity: 0 });
      renderCart();
      showToast(`${item ? item.product_title : 'Article'} retiré du panier`);
    } catch (err) {
      showToast(err.message);
      btn.disabled = false;
    }
  });

  async function addToCart(variantId, name, button) {
    if (!variantId) return;
    if (button) button.disabled = true;
    try {
      await cartRequest(`${routes.cartAdd}.js`, { items: [{ id: Number(variantId), quantity: 1 }] });
      await loadCart();
      cartCountEl.classList.remove('bump');
      void cartCountEl.offsetWidth;
      cartCountEl.classList.add('bump');
      showToast(`${name} ajouté au panier`);
    } catch (err) {
      showToast(err.message);
    } finally {
      if (button) button.disabled = false;
    }
  }
  function bindAddToCart(container) {
    container.querySelectorAll('.add-to-cart').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => addToCart(btn.dataset.variantId, btn.dataset.name, btn));
    });
  }
  bindAddToCart(document);
  loadCart().catch(() => {});

  // Product page: same Ajax add, the page stays in place.
  const productForm = document.getElementById('productForm');
  if (productForm) {
    productForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const title = document.querySelector('.product-page-title');
      addToCart(productForm.querySelector('[name="id"]').value, title ? title.textContent.trim() : '', productForm.querySelector('[type="submit"]'));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Category page — sorting handled by Shopify (?sort_by=)             */
  /* ------------------------------------------------------------------ */
  const sortSelect = document.getElementById('sortSelect');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Newsletter — Shopify customer form (subscribers in Customers)      */
  /* ------------------------------------------------------------------ */
  const NEWSLETTER_SEEN_KEY = 'aep-newsletter-seen';
  const seenPopup = () => { try { return !!sessionStorage.getItem(NEWSLETTER_SEEN_KEY); } catch (e) { return true; } };
  const markPopupSeen = () => { try { sessionStorage.setItem(NEWSLETTER_SEEN_KEY, '1'); } catch (e) {} };

  if (new URLSearchParams(location.search).get('customer_posted') === 'true') {
    markPopupSeen();
    showToast('Merci, à bientôt dans votre boîte mail');
  }

  const popupBackdrop = document.getElementById('popupBackdrop');
  const newsletterPopup = document.getElementById('newsletterPopup');
  if (newsletterPopup) {
    const openPopup = () => {
      popupBackdrop.classList.add('is-open');
      newsletterPopup.classList.add('is-open');
    };
    const closePopup = () => {
      popupBackdrop.classList.remove('is-open');
      newsletterPopup.classList.remove('is-open');
      markPopupSeen();
    };
    document.getElementById('popupClose').addEventListener('click', closePopup);
    document.getElementById('popupLater').addEventListener('click', closePopup);
    popupBackdrop.addEventListener('click', closePopup);
    newsletterPopup.querySelector('form').addEventListener('submit', markPopupSeen);
    // After a sign-up, Shopify comes back on the form that was sent (#newsletter-popup or the page section):
    // its message is shown there, the other form's copy is removed.
    const fromPopup = location.hash === '#newsletter-popup';
    document.querySelectorAll('[data-newsletter-msg]').forEach((msg) => {
      if (!!msg.closest('#newsletterPopup') !== fromPopup) msg.remove();
    });
    if (fromPopup && newsletterPopup.querySelector('[data-newsletter-msg]')) openPopup();
    else if (!designMode && !seenPopup()) setTimeout(openPopup, 5000);
  }

  /* ------------------------------------------------------------------ */
  /* Internal anchor links use smooth scroll                            */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('a[href*="#"]').forEach((a) => {
    const href = a.getAttribute('href');
    if (!isSamePageAnchor(href)) return;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      goTo(href);
    });
  });

  /* ------------------------------------------------------------------ */
  /* Theme editor: sections re-rendered live get their behaviours back  */
  /* ------------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', (e) => {
    setupReveal(e.target);
    setupJournal(e.target);
    bindHoverCursor(e.target);
    bindAddToCart(e.target);
    ScrollTrigger.refresh();
  });
})();
